import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import { ISelectedSessionDetails } from "repoV2/features/Common/modules/FormData/interfaces/SessionDetailsFormGroup.interfaces";
import { IUserDetails } from "repoV2/features/Common/modules/FormData/interfaces/UserDetailsFormGroup.interfaces";
import { IRenewSubscriptionBookingDetails } from "repoV2/features/Common/modules/FormData/interfaces/IRenewSubscriptionDetails";
import { logError } from "repoV2/utils/common/error/error";
import { HTTP_STATUS_CLIENT_ERROR_CODES } from "repoV2/constants/apis";
import { IFormDataState } from "repoV2/features/Common/modules/FormData/interfaces/FormData";
import { IDeliveryDetails } from "repoV2/features/Common/modules/FormData/interfaces/IDeliveryDetails.interface";
import { IGSTDetails } from "repoV2/features/Common/modules/FormData/interfaces/GSTDetailsFormGroup.interfaces";
import { IOrderBumpsDetails } from "repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/FormGroups/modules/OrderBumpsFormGroup/OrderBumpsFormGroup.interfaces";
import { IVariant } from "repoV2/features/Listing/Listing.interfaces";
import { renew_subscription_status_error } from "repoV2/features/CheckoutPages/modules/RenewSubscriptionSection/RenewSubscriptionSection.constants";
import {
    getLocalStorageItem,
    removeLocalStorageItem,
} from "repoV2/utils/common/storage/getterAndSetters";
import { LOCAL_STORAGE_KEYS as local_storage_keys } from "repoV2/features/Common/modules/Storage/Storage.constants";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import { LOCAL_STORAGE_KEYS } from "repoV2/constants/common/storage/localStorage";
import {
    ICallInitiateBookingCallback,
    ICallPaymentGatewayHOCArgs,
    IInitiateBookingPayload,
} from "../Payments.interface";
import { generatePaymentPayload } from "./generatePaymentPayload";
import { callInitiateBookingApi } from "./callInitiateBookingApi";
import { callPaymentGatewayHOC } from "./callPaymentGatewayHOC";

const useInitiateBooking = () => {
    const { notify, toast_type_keys } = useToastMessage();

    const initiateBooking = async (data: {
        userDetails?: { [userId: string]: IUserDetails };
        deliveryDetails?: IDeliveryDetails;
        renewSubscriptionBookingDetails?: IRenewSubscriptionBookingDetails;
        sessionDetails?: ISelectedSessionDetails;
        gstDetails: IGSTDetails;
        variantDetails?: IVariant;
        addressDetailsPayloadHelper?: {
            isSendAddressDetails?: boolean;
            showAddressInsideUserDetails?: boolean;
        };
        listingUuid: string;
        orderBumpsDetails?: IOrderBumpsDetails;
        allListingDetails: IFormDataState["listingDetails"];
        listingType: number;
        merchandiseQuantity?: number;
        paymentMethod: string;
        firstUserId?: string;
        installmentDetails?: {
            installmentUuid?: string;
            paymentType?: number;
        };
        thankyouUuid?: string;
        communityUuid?: string;
        dynamicLinkUuid?: string;
        multipleUsersHeadCount?: number;
        callPaymentGatewayOptionalCallbacks: ICallPaymentGatewayHOCArgs["optionalCallbacks"];
        callInitiateBookingOptionalCallbacks?: ICallInitiateBookingCallback;
    }) => {
        const { callInitiateBookingOptionalCallbacks } = data;
        // First call the 'generatePaymentPayload' method to get
        // the payload of the initiate payment api

        const affiliateUuid = getLocalStorageItem(
            local_storage_keys.AFFILIATE_UUID
        );

        const paymentPayload: IInitiateBookingPayload =
            generatePaymentPayload(data);

        if (!checkIsStringEmpty(affiliateUuid)) {
            paymentPayload.affiliate_uuid = affiliateUuid;
        }

        try {
            // Calling the initiate payment api
            const response = await callInitiateBookingApi({
                paymentPayload,
            });

            // Todo: In checkout p2, instead of removing set this after getting from api response
            removeLocalStorageItem(LOCAL_STORAGE_KEYS.USER_NAME_LIST);

            // Calling the payment gateway, after initiate payment based on its response
            // we call the specific payment gateway, and also pass the return url of the page
            // to redirect the user after the payment completion
            callPaymentGatewayHOC({
                listingDetails: { uuid: data?.listingUuid },
                returnUrl: paymentPayload.return_url,
                optionalCallbacks: data.callPaymentGatewayOptionalCallbacks,
            })({
                response: response.data,
            });
        } catch (error: any) {
            const errorObj = { ...error };
            const showApiErrorMessage =
                errorObj?.response?.status ===
                    HTTP_STATUS_CLIENT_ERROR_CODES.HTTP_STATUS_BAD_REQUEST &&
                errorObj?.response?.data?.message;
            if (
                errorObj?.response?.data?.data?.error_status ===
                    renew_subscription_status_error &&
                callInitiateBookingOptionalCallbacks?.showRenewSubscriptionError
            ) {
                callInitiateBookingOptionalCallbacks?.showRenewSubscriptionError();
            }
            notify(
                showApiErrorMessage
                    ? errorObj?.response?.data?.message
                    : "Something went wrong! Try again!",
                {
                    type: toast_type_keys.error,
                }
            );

            // If the api fails, then we stop the loader
            if (
                callInitiateBookingOptionalCallbacks?.stopBookingButtonProcessing
            ) {
                callInitiateBookingOptionalCallbacks.stopBookingButtonProcessing();
            }

            logError({
                error,
                occuredAt:
                    "initiatePayment at repoV2/features/CheckoutPages/utils/apis/CheckoutPaymentApi/CheckoutPayment.ts",
                when: "calling payment initiate api",
            });
        }
    };

    return {
        initiateBooking,
    };
};

export default useInitiateBooking;
